import { useState } from 'react';
import type { EventoCalendario, PlantillaResumen } from '@matias-asset-os/contracts';
import { t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { Modal } from './Modal.js';

/**
 * EDITAR UNA VISITA PROGRAMADA.
 *
 * Hasta ahora, de una cita de la agenda sólo se podía cambiar el estado y
 * quitarla entera. Todo lo demás —la fecha, la hora, quién va, qué lista se
 * recorre— estaba escrito en piedra desde el momento de programarla. Y lo que
 * más cambia de una visita es justamente la fecha: llueve, el cliente se
 * retrasa, hay que ir dos días antes.
 *
 * Así que se borraba y se volvía a crear. Eso funciona, pero pierde el rastro:
 * la lista que ya se hubiera empezado se queda huérfana y nadie sabe que esa
 * visita es la misma de la semana pasada movida.
 */

export function ModalEditarVisita({
  idioma,
  evento,
  usuarios,
  plantillas,
  onHecho,
  onCerrar,
}: {
  idioma: Idioma;
  evento: EventoCalendario;
  usuarios: Array<{ id: string; nombre: string }>;
  plantillas: PlantillaResumen[];
  onHecho: () => void;
  onCerrar: () => void;
}) {
  const [titulo, setTitulo] = useState(evento.titulo);
  const [tipo, setTipo] = useState(evento.tipo);
  const [plantillaId, setPlantillaId] = useState(evento.plantillaId ?? '');
  const [fecha, setFecha] = useState(evento.fecha);
  const [hora, setHora] = useState(evento.hora ?? '');
  const [asignado, setAsignado] = useState(evento.asignadoUserId ?? '');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const activas = plantillas.filter((p) => p.activo);

  return (
    <Modal idioma={idioma} titulo={t(idioma, 'visita.editar')} icono="calendario" onCerrar={onCerrar}>
      <form
        className="formulario"
        style={{ maxWidth: 'none' }}
        onSubmit={(e) => {
          e.preventDefault();
          setEnviando(true);
          setError(null);
          api
            .editarEvento(evento.id, {
              titulo: titulo.trim(),
              tipo,
              fecha,
              hora: hora || null,
              asignadoUserId: asignado || null,
              plantillaId: plantillaId || null,
            })
            .then(() => {
              onHecho();
              onCerrar();
            })
            .catch((f: unknown) =>
              setError(
                f instanceof ErrorApi ? t(idioma, f.mensajeClave) : t(idioma, 'error.servidor'),
              ),
            )
            .finally(() => setEnviando(false));
        }}
      >
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <div className="campo">
          <label htmlFor="ev-plantilla">{t(idioma, 'visita.plantilla')}</label>
          <select
            id="ev-plantilla"
            value={plantillaId}
            onChange={(e) => {
              setPlantillaId(e.target.value);
              /*
                Cambiar la lista cambia el nombre de la cita, salvo que alguien
                le haya puesto uno a mano: el titulo es el nombre de la lista.
              */
              const elegida = activas.find((p) => p.id === e.target.value);
              if (elegida && titulo.trim() === (evento.plantillaNombre ?? evento.titulo)) {
                setTitulo(elegida.nombre);
              }
            }}
          >
            <option value="">{t(idioma, 'visita.sinPlantilla')}</option>
            {activas.map((p) => (
              <option key={p.id} value={p.id}>
                {p.nombre}
              </option>
            ))}
          </select>
        </div>

        <div className="campo">
          <label htmlFor="ev-titulo">{t(idioma, 'evento.titulo')}</label>
          <input
            id="ev-titulo"
            type="text"
            value={titulo}
            maxLength={200}
            required
            onChange={(e) => setTitulo(e.target.value)}
          />
        </div>

        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="ev-fecha">{t(idioma, 'evento.fecha')}</label>
            <input
              id="ev-fecha"
              type="date"
              value={fecha}
              required
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor="ev-hora">{t(idioma, 'evento.hora')}</label>
            <input id="ev-hora" type="time" value={hora} onChange={(e) => setHora(e.target.value)} />
          </div>
        </div>

        <div className="rejilla dos">
          <div className="campo">
            <label htmlFor="ev-tipo">{t(idioma, 'evento.tipo')}</label>
            <select
              id="ev-tipo"
              value={tipo}
              onChange={(e) => setTipo(e.target.value as EventoCalendario['tipo'])}
            >
              {['VISITA', 'PREVENTIVA', 'REVISION', 'LLEGADA', 'SALIDA', 'OTRO'].map((x) => (
                <option key={x} value={x}>
                  {t(idioma, `evento.${x}`)}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor="ev-asig">{t(idioma, 'evento.asignado')}</label>
            <select id="ev-asig" value={asignado} onChange={(e) => setAsignado(e.target.value)}>
              <option value="">{t(idioma, 'evento.sinAsignar')}</option>
              {usuarios.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="fila-flex" style={{ gap: '0.5rem' }}>
          <button type="submit" className="acceso-entrar" disabled={enviando || !titulo.trim()}>
            {t(idioma, 'app.guardar')}
          </button>
          <button type="button" className="secundario" onClick={onCerrar}>
            {t(idioma, 'app.cancelar')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
